// Top-down radar with a baked terrain background.
//
// The world map is baked once per world load (in Minimap.ts) into a 384×384
// image. Each frame we draw it with a single transformed drawImage call:
//   translate to radar center → rotate by -(heading + π/2) so the player's
//   forward axis points "up" → scale to (radarPixels / RADAR_RANGE) → translate
//   so the player's world position lands at the origin → drawImage at world
//   coords (-size/2, -size/2, size, size). Linear in pixels.

import * as THREE from "three";
import type { PlayerSnapshot } from "../shared/protocol";
import { TERRAIN } from "../shared/protocol";

const RADAR_RANGE = 1400; // world units mapped to radar edge

export class Radar {
  private cvs: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number;
  private mapImage: HTMLCanvasElement | null = null;

  constructor() {
    this.cvs = document.getElementById("radar") as HTMLCanvasElement;
    this.ctx = this.cvs.getContext("2d")!;
    this.size = this.cvs.width;
  }

  setMap(canvas: HTMLCanvasElement | null) {
    this.mapImage = canvas;
  }

  draw(players: PlayerSnapshot[], myId: string, myQuat: THREE.Quaternion) {
    const ctx = this.ctx;
    const s = this.size;
    const cx = s / 2, cy = s / 2;
    ctx.clearRect(0, 0, s, s);

    const me = players.find(p => p.id === myId);
    const fwd = new THREE.Vector3(1, 0, 0).applyQuaternion(myQuat);
    const headingAngle = Math.atan2(fwd.z, fwd.x);

    // ===== 1) Baked terrain map (heading-locked background) =====
    if (this.mapImage && me) {
      ctx.save();
      // Clip to a circle so the map doesn't bleed past the radar frame.
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.47, 0, Math.PI * 2);
      ctx.clip();
      // Heading-locked rotation: forward = up.
      ctx.translate(cx, cy);
      ctx.rotate(-(headingAngle + Math.PI / 2));
      const radarPerWorld = (s * 0.47) / RADAR_RANGE;
      ctx.scale(radarPerWorld, radarPerWorld);
      ctx.translate(-me.x, -me.z);
      // Draw the world image at its world-space position.
      ctx.drawImage(this.mapImage, -TERRAIN.size / 2, -TERRAIN.size / 2, TERRAIN.size, TERRAIN.size);
      ctx.restore();
    }

    // ===== 2) Range rings + crosshair =====
    ctx.strokeStyle = "rgba(120, 180, 255, 0.18)";
    ctx.lineWidth = 1;
    for (let r = s * 0.18; r <= s * 0.45; r += s * 0.18) {
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(cx, 0); ctx.lineTo(cx, s);
    ctx.moveTo(0, cy); ctx.lineTo(s, cy);
    ctx.stroke();

    // Heading marker (forward indicator at top)
    ctx.fillStyle = "rgba(92, 242, 255, 0.85)";
    ctx.beginPath();
    ctx.moveTo(cx, 6); ctx.lineTo(cx - 5, 14); ctx.lineTo(cx + 5, 14); ctx.closePath();
    ctx.fill();

    // ===== 3) Other players (red dots, with charging/shield rings) =====
    if (me) {
      for (const p of players) {
        if (p.id === myId) continue;
        if (!p.alive) continue;
        const dx = p.x - me.x;
        const dz = p.z - me.z;
        const dist = Math.hypot(dx, dz);
        const clipped = Math.min(dist, RADAR_RANGE);
        const rel = Math.atan2(dz, dx) - headingAngle;
        const px = cx + Math.sin(rel) * (clipped / RADAR_RANGE) * (s * 0.45);
        const py = cy - Math.cos(rel) * (clipped / RADAR_RANGE) * (s * 0.45);
        ctx.fillStyle = dist > RADAR_RANGE ? "rgba(255, 80, 80, 0.45)" : "rgba(255, 80, 80, 1)";
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fill();
        if (p.laserChargeT > 0) {
          ctx.strokeStyle = "rgba(255, 100, 100, 0.95)";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(px, py, 8, 0, Math.PI * 2);
          ctx.stroke();
        }
        if (p.shieldT > 0) {
          ctx.strokeStyle = "rgba(92, 242, 255, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(px, py, 9, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }

    // ===== 4) Self =====
    ctx.fillStyle = "rgba(92, 242, 255, 1)";
    ctx.beginPath();
    ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }
}
